import React from "react";
import { useSelector } from "react-redux";
import { Descriptions, Row, Col, Button, Space } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import NumberDisplay from "components/NumberDisplay";
import { Section } from "components/QnaComponents";
import { downloadFile } from "utils/common";
import { getAttachmentById } from "api";
import { selectApprovalSummary } from "reducers";

export const fetchAttachmentById = async (attachmentId) => {
	try {
		downloadFile(await getAttachmentById({ attachmentId }));
	} catch (error) {
		console.log(error.message);
	}
};

const rowStyle = {
	padding: "5px 5px",
	borderBottom: "1px solid #eee",
};
const colStyle = { paddingRight: "3px" };

const ApprovalSummary = () => {
	const {
		projectCode,
		projectShortName,
		projectLongName,
		division,
		rc,
		startDate,
		endDate,
		FPIMSetupType,
		projectBudget,
		fundedFrom,
		fileList,
		keyMembers,
		projectOutline,
		riskAddressed,
		alternatives,
		contactPerson,
		deliveryAddress,
		opex,
	} = useSelector(selectApprovalSummary);

	const onPreview = async (file) => {
		try {
			await fetchAttachmentById(file.uid);
		} catch (e) {
			console.log(e);
		}
	};

	return (
		<Section title="Summary View" size="large">
			<Descriptions
				column={1}
				size="medium"
				labelStyle={{
					fontWeight: 600,
					width: "200px",
				}}
				contentStyle={{
					backgroundColor: "white",
				}}
			>
				<Descriptions.Item label="Project Code">
					{projectCode}
				</Descriptions.Item>
				<Descriptions.Item label="Project Name">
					{"WT-23-" + projectShortName}
				</Descriptions.Item>
				<Descriptions.Item label="Project Long Name">
					{"WT-23-" + projectLongName}
				</Descriptions.Item>
				<Descriptions.Item label="Project Summary">
					<Space direction="vertical">
						<i>Outline</i>
						{projectOutline}
						<i>What is the risk being addressed?</i>
						{riskAddressed}
						<i>
							What are the alternatives, including consequences of
							not doing the project?
						</i>
						{alternatives}
					</Space>
				</Descriptions.Item>
				<Descriptions.Item label="Service">
					{division}
				</Descriptions.Item>
				<Descriptions.Item label="Responsibility Centre">
					{rc}
				</Descriptions.Item>
				<Descriptions.Item label="Start Date">
					{startDate}
				</Descriptions.Item>
				<Descriptions.Item label="End Date">
					{endDate}
				</Descriptions.Item>
				<Descriptions.Item label="FPIM Setup Type">
					{FPIMSetupType}
				</Descriptions.Item>
				{contactPerson && (
					<>
						<Descriptions.Item label="Contact Person">
							{contactPerson}
						</Descriptions.Item>
						<Descriptions.Item label="Delivery Address">
							{deliveryAddress}
						</Descriptions.Item>
					</>
				)}
				<Descriptions.Item label="Project Budget">
					<div style={{ width: 750 }}>
						<Row
							style={{
								fontStyle: "italic",
								...rowStyle,
							}}
						>
							<Col span={6} style={colStyle}>
								Item Description
							</Col>
							<Col span={6} style={colStyle}>
								Minor Cat.
							</Col>
							<Col
								span={2}
								style={{ textAlign: "right", ...colStyle }}
							>
								Quantity
							</Col>
							<Col
								span={3}
								style={{ textAlign: "right", ...colStyle }}
							>
								Unit price
							</Col>
							<Col
								span={3}
								style={{ textAlign: "right", ...colStyle }}
							>
								Total
							</Col>
							<Col
								span={2}
								style={{
									textAlign: "right",
									...colStyle,
								}}
							>
								WIP Account
							</Col>
							<Col
								span={2}
								style={{
									textAlign: "right",
									...colStyle,
								}}
							>
								Life (Yrs)
							</Col>
						</Row>
						{projectBudget.map(
							({
								id,
								itemDescription,
								minor,
								quantity,
								unitPrice,
								total,
								wipAccount,
								lifeYears,
							}) => {
								return (
									<Row key={id} style={rowStyle}>
										<Col span={6} style={colStyle}>
											{itemDescription}
										</Col>
										<Col span={6} style={colStyle}>
											{minor}
										</Col>
										<Col span={2} style={colStyle}>
											<NumberDisplay
												number={quantity}
												currency={false}
											/>
										</Col>
										<Col span={3} style={colStyle}>
											<NumberDisplay
												number={unitPrice}
												maxDigits={0}
											/>
										</Col>
										<Col span={3} style={colStyle}>
											<NumberDisplay
												number={total}
												maxDigits={0}
											/>
										</Col>
										<Col
											span={2}
											style={{
												textAlign: "right",
												...colStyle,
											}}
										>
											{wipAccount}
										</Col>
										<Col
											span={2}
											style={{
												textAlign: "right",
												...colStyle,
											}}
										>
											{lifeYears}
										</Col>
									</Row>
								);
							}
						)}
						<Row style={{ fontWeight: 600, ...rowStyle }}>
							<Col span={6} style={colStyle}>
								Total
							</Col>
							<Col span={6} style={colStyle}></Col>
							<Col span={2} style={colStyle}></Col>
							<Col
								span={3}
								style={{ textAlign: "right", ...colStyle }}
							></Col>
							<Col
								span={3}
								style={{ textAlign: "right", ...colStyle }}
							>
								<NumberDisplay
									number={Math.ceil(
										projectBudget.reduce(
											(accum, current) => {
												return accum + current?.total;
											},
											0
										)
									)}
									maxDigits={0}
								/>
							</Col>
							<Col
								span={2}
								style={{
									textAlign: "right",
									...colStyle,
								}}
							></Col>
							<Col
								span={2}
								style={{
									textAlign: "right",
									...colStyle,
								}}
							></Col>
						</Row>
					</div>
				</Descriptions.Item>
				<Descriptions.Item label="Associated Additional Operating Costs">
					<div style={{ width: 750 }}>
						<Row
							style={{
								fontStyle: "italic",
								...rowStyle,
							}}
						>
							<Col span={12} style={colStyle}>
								Item Description
							</Col>
							<Col
								span={4}
								style={{ textAlign: "right", ...colStyle }}
							>
								No. of Years
							</Col>
							<Col
								span={4}
								style={{ textAlign: "right", ...colStyle }}
							>
								Amount Per Year
							</Col>
							<Col
								span={4}
								style={{ textAlign: "right", ...colStyle }}
							>
								Total
							</Col>
						</Row>
						{opex.map(
							({
								id,
								costType,
								years,
								amountPerYear,
								totalOngoingCost,
							}) => {
								return (
									<Row key={id} style={rowStyle}>
										<Col span={12} style={colStyle}>
											{costType}
										</Col>
										<Col span={4} style={colStyle}>
											<NumberDisplay
												number={years}
												currency={false}
											/>
										</Col>
										<Col span={4} style={colStyle}>
											<NumberDisplay
												number={amountPerYear}
												maxDigits={0}
											/>
										</Col>
										<Col span={4} style={colStyle}>
											<NumberDisplay
												number={totalOngoingCost}
												maxDigits={0}
											/>
										</Col>
									</Row>
								);
							}
						)}
						<Row style={{ fontWeight: 600, ...rowStyle }}>
							<Col span={12} style={colStyle}>
								Total
							</Col>
							<Col span={4} style={colStyle}></Col>
							<Col span={4} style={colStyle}></Col>
							<Col
								span={4}
								style={{ textAlign: "right", ...colStyle }}
							>
								<NumberDisplay
									number={Math.ceil(
										opex.reduce((accum, current) => {
											return (
												accum +
												current?.totalOngoingCost
											);
										}, 0)
									)}
									maxDigits={0}
								/>
							</Col>
						</Row>
					</div>
				</Descriptions.Item>
				<Descriptions.Item label="Funded From">
					<div style={{ width: 750 }}>
						<Row style={{ fontStyle: "italic", ...rowStyle }}>
							<Col span={9} colstyle={colStyle}>
								Source Project
							</Col>
							<Col span={6} colstyle={colStyle}>
								Code
							</Col>
							<Col span={6} colstyle={colStyle}>
								WT Project Code
							</Col>
							<Col
								span={3}
								style={{
									textAlign: "right",
									...colStyle,
								}}
							>
								Amount
							</Col>
						</Row>
						{fundedFrom.map((f) => {
							return (
								<Row key={f.id} style={rowStyle}>
									<Col span={9} style={colStyle}>
										{f?.source}
									</Col>
									<Col span={6} style={colStyle}>
										{f?.code}
									</Col>
									<Col span={6} style={colStyle}>
										{f?.wtProjectCode}
									</Col>
									<Col
										span={3}
										style={{
											textAlign: "right",
											...colStyle,
										}}
									>
										<NumberDisplay
											number={f?.amount}
											maxDigits={0}
										/>
									</Col>
								</Row>
							);
						})}
						<Row style={{ fontWeight: 600, ...rowStyle }}>
							<Col span={21} style={colStyle}>
								Total
							</Col>
							<Col
								span={3}
								style={{
									textAlign: "right",
									...colStyle,
								}}
							>
								<NumberDisplay
									currency
									number={Math.ceil(
										fundedFrom.reduce((accum, current) => {
											return accum + current?.amount;
										}, 0)
									)}
									maxDigits={0}
								/>
							</Col>
						</Row>
					</div>
				</Descriptions.Item>
				<Descriptions.Item label="Attachments">
					<Space direction="vertical">
						{fileList &&
							fileList.length > 0 &&
							fileList.map((a) => {
								return (
									<Button
										key={a.uid}
										icon={<PaperClipOutlined />}
										type="link"
										onClick={() => onPreview(a)}
									>
										{a.name}
									</Button>
								);
							})}
					</Space>
				</Descriptions.Item>
				<Descriptions.Item label="Key Members">
					<div style={{ width: 750 }}>
						<Row
							style={{
								fontStyle: "italic",
								...rowStyle,
							}}
						>
							<Col span={7}>Name</Col>
							<Col span={7}>Role</Col>
							<Col span={3}>Approval Limit ($)</Col>
							<Col span={7}>Limit to Project or Task</Col>
						</Row>
						{keyMembers &&
							keyMembers.length > 0 &&
							keyMembers.map((k) => {
								return (
									<Row key={k.id} style={rowStyle}>
										<Col span={7}>{k.fullName}</Col>
										<Col span={7}>{k.role}</Col>
										<Col span={3}>{k.limit}</Col>
										<Col span={7}>{k.limitTo}</Col>
									</Row>
								);
							})}
					</div>
				</Descriptions.Item>
			</Descriptions>
		</Section>
	);
};

export default ApprovalSummary;
